import { Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CreditCard,
  FileText,
  Globe,
  Landmark,
  MapPin,
  Monitor,
  Phone,
  ShoppingCart,
  Sparkles,
  Star,
} from "lucide-react";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import {
  CHOICE_BLOCK,
  FINAL_CTA,
  PROOF_STRIP,
  REVIEW_BLOCK,
  SOLUTIONS_BLOCK,
  SUPPORT_BLOCK,
} from "@/data/locationsContent";

/**
 * Shared sections for /locations and /locations/{city} (handover v4).
 *
 * Every block here renders exact approved copy from data/locationsContent.js.
 * The pages compose them in the order the handover fixes; keep the blocks
 * presentational so the copy module stays the single source of wording.
 *
 * The /industries pages (handover v3, Sep 2026) reuse these blocks with their
 * own copy passed through the `content` / `support` / `review` props; the
 * defaults stay the Locations copy.
 */

const CONTAINER = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
const EYEBROW = "text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-3";
const H2 = "font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight";
const BTN_PRIMARY =
  "bg-brand-aqua-deep hover:bg-brand-aqua-deeper text-white font-semibold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-colors btn-shine";
const BTN_SECONDARY =
  "border border-brand-light-grey hover:border-brand-aqua/50 bg-white text-brand-black font-semibold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-colors";
const TEXT_LINK =
  "inline-flex items-center gap-1 text-sm font-semibold text-brand-aqua-deep hover:text-brand-black transition-colors";

/**
 * Render approved copy that may contain `[label](/path)` links as text with
 * client-side <Link>s. Anything else is emitted verbatim.
 */
export function InlineCopy({ text }) {
  // Drop empty parts: copy that opens with a link would otherwise emit an
  // empty string and the link at the same offset (duplicate key).
  const parts = String(text || "").split(/(\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  // Key by character offset: stable for a given string and unique per part.
  let offset = 0;
  return (
    <>
      {parts.map((part) => {
        const key = `${offset}`;
        offset += part.length;
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (!m) return <Fragment key={key}>{part}</Fragment>;
        return (
          <Link key={key} to={m[2]} className="font-semibold text-brand-aqua-deep underline underline-offset-2 hover:text-brand-black transition-colors">
            {m[1]}
          </Link>
        );
      })}
    </>
  );
}

/* ─── Customer-choice cards (used once per page) ──────────────────────── */

function ChoiceCard({ icon: Icon, heading, body, cta, link, support, testId }) {
  return (
    <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-7 shadow-sm flex flex-col" data-testid={testId}>
      <div className="w-11 h-11 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-4">
        <Icon size={20} className="text-brand-aqua-deep" />
      </div>
      <h3 className="font-heading text-xl font-bold text-brand-black mb-2">{heading}</h3>
      <p className="text-sm text-brand-grey-dark leading-relaxed mb-5 flex-1">{body}</p>
      <Link to={cta.to} className={BTN_PRIMARY} data-testid={`${testId}-cta`}>
        {cta.label}<ArrowRight size={16} />
      </Link>
      {link && (
        <Link to={link.to} className={`${TEXT_LINK} mt-3`} data-testid={`${testId}-link`}>
          {link.label}<ArrowRight size={14} />
        </Link>
      )}
      {support && <p className="text-xs text-brand-grey-dark mt-3">{support}</p>}
    </div>
  );
}

/**
 * The two customer journeys. `embedded` renders the cards without the
 * section wrapper/eyebrow so a city hero can use them as its action area.
 */
export function LocationChoiceBlock({ embedded = false, content = CHOICE_BLOCK, testId = "location-choice" }) {
  const cards = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5" data-testid={testId}>
      <ChoiceCard icon={FileText} {...content.existing} testId={`${testId}-existing`} />
      <ChoiceCard icon={Sparkles} {...content.newBusiness} testId={`${testId}-new`} />
    </div>
  );
  if (embedded) {
    // City heroes keep the compact layout (no visible section heading) but
    // still expose the block's H2 so the two H3 cards sit under a heading
    // in the document outline.
    return (
      <div>
        <h2 className="sr-only">{content.heading}</h2>
        {cards}
      </div>
    );
  }
  return (
    <section className="py-14 md:py-16 bg-gray-50">
      <div className={CONTAINER}>
        <div className="text-center mb-8">
          <p className={EYEBROW}>{content.eyebrow}</p>
          <h2 className={H2}>{content.heading}</h2>
        </div>
        <div className="max-w-5xl mx-auto">{cards}</div>
      </div>
    </section>
  );
}

/* ─── Qualified proof strip ──────────────────────────────────────────── */

export function LocationProofStrip({ content = PROOF_STRIP, testId = "location-proof-strip" }) {
  return (
    <section className="py-6 bg-white border-y border-brand-light-grey/50" data-testid={testId}>
      <div className={CONTAINER}>
        <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 list-none p-0 m-0">
          {content.items.map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm font-semibold text-brand-black">
              <Star size={14} className="text-brand-aqua flex-shrink-0" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-xs text-brand-grey-dark text-center mt-3">{content.note}</p>
      </div>
    </section>
  );
}

/* ─── Six payment-solution cards ─────────────────────────────────────── */

const SOLUTION_ICONS = {
  "/card-machines": CreditCard,
  "/epos-systems": Monitor,
  "/online-payments": Globe,
  "/virtual-terminal": Phone,
  "/online-ordering": ShoppingCart,
  "/merchant-cash-advance": Landmark,
};

export function LocationSolutionsBlock({ locationName, testId = "location-solutions" }) {
  return (
    <section className="py-14 md:py-16 bg-gray-50" data-testid={testId}>
      <div className={CONTAINER}>
        <div className="text-center mb-8 max-w-2xl mx-auto">
          <p className={EYEBROW}>{SOLUTIONS_BLOCK.eyebrow}</p>
          <h2 className={H2}>{SOLUTIONS_BLOCK.headingFor(locationName)}</h2>
          <p className="text-sm text-brand-grey-dark mt-3">{SOLUTIONS_BLOCK.intro}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SOLUTIONS_BLOCK.items.map((s) => {
            const Icon = SOLUTION_ICONS[s.to] || CreditCard;
            return (
              <Link
                key={s.to}
                to={s.to}
                className="group bg-white border border-brand-light-grey rounded-2xl p-5 hover:border-brand-aqua/50 hover:shadow-md transition-all"
                data-testid={`${testId}-${s.to.replace("/", "")}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-brand-aqua-deep" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-brand-black mb-1 flex items-center gap-1.5">
                      {s.heading}
                      <ArrowRight size={14} className="text-brand-aqua opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    </h3>
                    <p className="text-sm text-brand-grey-dark leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Local support + customer-review link (rendered together) ───────── */

export function LocationSupportAndReviews({
  support = SUPPORT_BLOCK,
  review = REVIEW_BLOCK, // pass null to render the support card on its own
  bg = "bg-white",
  testId = "location-support",
}) {
  const cardBg = bg === "bg-white" ? "bg-gray-50" : "bg-white";
  return (
    <section className={`py-14 md:py-16 ${bg}`} data-testid={testId}>
      <div className={CONTAINER}>
        <div className={`grid grid-cols-1 gap-6 lg:gap-8 ${review ? "lg:grid-cols-2" : "max-w-3xl"}`}>
          <div className={`${cardBg} border border-brand-light-grey rounded-2xl p-6 sm:p-8`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-brand-aqua-deep" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-deep">{support.eyebrow}</p>
            </div>
            <h2 className="font-heading text-2xl font-bold text-brand-black tracking-tight mb-3">{support.heading}</h2>
            <p className="text-sm text-brand-grey-dark leading-relaxed">{support.body}</p>
          </div>
          {review && (
            <div className={`${cardBg} border border-brand-light-grey rounded-2xl p-6 sm:p-8 flex flex-col`} data-testid={`${testId}-reviews`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                  <Star size={18} className="text-brand-aqua-deep" />
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-deep">{review.eyebrow}</p>
              </div>
              <h2 className="font-heading text-2xl font-bold text-brand-black tracking-tight mb-3">{review.heading}</h2>
              <p className="text-sm text-brand-grey-dark leading-relaxed mb-5 flex-1">{review.body}</p>
              <div>
                <Link to={review.cta.to} className={BTN_SECONDARY} data-testid={`${testId}-reviews-cta`}>
                  {review.cta.label}<ArrowRight size={16} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQs ───────────────────────────────────────────────────────────── */

export function LocationFaqs({ heading, faqs, testIdPrefix = "location-faq" }) {
  const items = faqs.map((f) => ({ q: f.q, a: <InlineCopy text={f.a} /> }));
  return (
    <section className="py-14 md:py-16 bg-white" data-testid={`${testIdPrefix}-section`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className={`${H2} mb-8 text-center`}>{heading}</h2>
        <ResponsiveAccordion items={items} testIdPrefix={testIdPrefix} headingLevel="h3" />
      </div>
    </section>
  );
}

/* ─── Final two actions + online helper ──────────────────────────────── */

export function LocationFinalCta({ content = FINAL_CTA, testId = "location-final-cta" }) {
  return (
    <section className="py-16 md:py-20 bg-brand-black text-white" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">{content.heading}</h2>
        <p className="text-white/80 leading-relaxed mb-8">{content.body}</p>
        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
          <Link to={content.primary.to} className={BTN_PRIMARY} data-testid={`${testId}-primary`}>
            {content.primary.label}<ArrowRight size={16} />
          </Link>
          <Link
            to={content.secondary.to}
            className="border border-white/30 hover:border-white text-white font-semibold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-colors"
            data-testid={`${testId}-secondary`}
          >
            {content.secondary.label}<ArrowRight size={16} />
          </Link>
        </div>
        <Link
          to={content.link.to}
          className="inline-flex items-center gap-1 text-sm font-semibold text-white/80 hover:text-white underline underline-offset-4 mt-5 transition-colors"
          data-testid={`${testId}-online`}
        >
          {content.link.label}<ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}
